import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import {
  getAnupanAction,
  deleteAnupanAction,
} from "../../../store/actions/AnupanAction";

const Anupan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isFirstLoad = useRef(true);

  const anupanList = useSelector((state) => state.anupan.list);
  const numRows = useSelector((state) => state.anupan.num_rows || 0);

  const [searchQueries, setSearchQueries] = useState({
    anupana_text: "",
    anupana_status: "",
  });

  const [sortField, setSortField] = useState("anupana_text");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  let searchTimeout = null;

  const fetchAnupans = async (
    search_by = "",
    search_keyword = "",
    page = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      if (isFirstLoad.current) setLoading(true);

      await dispatch(
        getAnupanAction(true, page, search_by, search_keyword, sort_by, sort_order)
      );
    } catch (err) {
      console.error("Failed to fetch Anupan:", err);
      setError("Failed to load Anupan list.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false;
    }
  };

  useEffect(() => {
    if (location?.state?.returnTo) {
      const {
        currentPage: prevPage,
        searchQueries: prevSearches,
        sortField: prevSort,
        sortOrder: prevOrder,
      } = location.state.returnTo;
      setCurrentPage(prevPage || 0);
      setSearchQueries(prevSearches || {});
      setSortField(prevSort || "anupana_text");
      setSortOrder(prevOrder || "asc");

      const searchField =
        prevSearches?.anupana_text
          ? "anupana_text"
          : prevSearches?.anupana_status
          ? "anupana_status"
          : "";
      const searchKeyword =
        prevSearches?.anupana_text || prevSearches?.anupana_status || "";

      setActiveSearchField(searchField);
      setActiveSearchKeyword(searchKeyword);

      fetchAnupans(
        searchField,
        searchKeyword,
        prevPage || 0,
        prevSort || "anupana_text",
        prevOrder || "asc"
      );
    } else {
      fetchAnupans();
    }
  }, []);

  const handleSearch = (e, field) => {
    const value = e.target.value;

    const cleared = { anupana_text: "", anupana_status: "" };
    const updated = { ...cleared, [field]: value };
    setSearchQueries(updated);
    setActiveSearchField(field);
    setActiveSearchKeyword(value);
    setCurrentPage(0);

    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      fetchAnupans(field, value, 0, sortField, sortOrder);
    }, 400);
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);

    fetchAnupans(activeSearchField, activeSearchKeyword, 0, field, order);
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This Anupan will be deleted!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (yes) => {
      if (yes) {
        try {
          setLoading(true);
          await dispatch(deleteAnupanAction(id));
          await fetchAnupans(
            activeSearchField,
            activeSearchKeyword,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete Anupan");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAnupans(activeSearchField, activeSearchKeyword, page, sortField, sortOrder);
  };

  const totalPages = Math.ceil(numRows / rowsPerPage);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/app-add-anupan" className="btn btn-primary">
            + Add Anupan
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="text-danger mb-2">{error}</div>}
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped mb-4">
                  <thead className="table-light">
                    <tr>
                      <th onClick={() => handleSort("anupana_text")} style={{ cursor: "pointer" }}>
                        Anupan
                        {sortField === "anupana_text" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.anupana_text}
                          onChange={(e) => handleSearch(e, "anupana_text")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Anupan"
                        />
                      </th>
                      <th onClick={() => handleSort("anupana_status")} style={{ cursor: "pointer" }}>
                        Status
                        {sortField === "anupana_status" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.anupana_status}
                          onChange={(e) => handleSearch(e, "anupana_status")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Status"
                        />
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {anupanList?.length > 0 ? (
                      anupanList.map((item) => (
                        <tr key={item.id}>
                          <td>{item.anupana_text}</td>
                          <td>
                            <span className={`badge ${item.anupana_status === "Active" ? "bg-success" : "bg-danger"}`}>
                              {item.anupana_status}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex">
                              <span
                                className="edit me-4"
                                onClick={() =>
                                  navigate("/app-add-anupan", {
                                    state: {
                                      item,
                                      returnTo: { currentPage, searchQueries, sortField, sortOrder },
                                    },
                                  })
                                }
                                style={{ cursor: "pointer" }}
                                title="Edit Anupan"
                              >
                                <i className="fa fa-pencil-alt text-success"></i>
                              </span>
                              <span
                                className="delete"
                                onClick={() => handleDelete(item.id)}
                                style={{ cursor: "pointer" }}
                                title="Delete Anupan"
                              >
                                <i className="fa fa-trash text-danger"></i>
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">
                          No Anupans Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div
                className="pagination-wrapper"
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "20px",
                  flexWrap: "wrap",
                  flexDirection: "column",
                }}
              >
                <div>
                  Showing {anupanList?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                  <button
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                    disabled={currentPage === 0}
                    style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff", color: "#000", cursor: currentPage === 0 ? "not-allowed" : "pointer", opacity: currentPage === 0 ? 0.5 : 1 }}
                  >
                    &lt;
                  </button>
                  {(() => {
                    const pages = [];
                    const maxPagesToShow = 5;
                    const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                    const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                    if (startPage > 0) {
                      pages.push(
                        <button key={0} onClick={() => handlePageChange(0)} style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff", color: "#000", cursor: "pointer" }}>
                          1
                        </button>
                      );
                      if (startPage > 1) {
                        pages.push(
                          <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                            ...
                          </span>
                        );
                      }
                    }

                    for (let i = startPage; i < endPage; i++) {
                      const isActive = currentPage === i;
                      pages.push(
                        <button key={i} onClick={() => handlePageChange(i)} style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: isActive ? "#007bff" : "#fff", color: isActive ? "#fff" : "#000", cursor: "pointer" }}>
                          {i + 1}
                        </button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button key={totalPages - 1} onClick={() => handlePageChange(totalPages - 1)} style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff", color: "#000", cursor: "pointer" }}>
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                  <button
                    onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                    disabled={currentPage === totalPages - 1}
                    style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff", color: "#000", cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer", opacity: currentPage === totalPages - 1 ? 0.5 : 1 }}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Anupan;
